// components/Compliance/StudentCompliancePage.js
// ──────────────────────────────────────────────────────────────────────────────
// Student-facing list of mandatory training assignments. Shows due-date,
// status, and a clear "Start / Continue" CTA into the assigned course.
// Overdue blocking trainings are highlighted at the top.
// ──────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, PlayCircle, Clock } from 'lucide-react';
import {
  PageHeader, AppButton, StatusBadge, Callout, AppDataTable, sortRows,
} from '../ui';
import { getMyMandatoryAssignments } from '../../utils/complianceApi';

/**
 * ⚠️ This is a STUDENT page, and it gets the table's chrome but not its
 * controls — no column filters, no resize handles, no paginator.
 *
 * A student has a handful of mandatory trainings, not a list they need to
 * interrogate. Funnels over four rows are furniture that filters nothing, and
 * a resize handle on a screen someone visits twice a term is a control that
 * only ever gets dragged by accident. Sorting stays, because "what is due
 * first" is a real question at any length.
 *
 * The admin list next door (`MandatoryTrainingList`) is where the full set
 * belongs. Same component, proportionate controls — see
 * `docs/design-system/TABLE_MIGRATION.md`.
 *
 * ⚠️ No `_compliance.scss` import. The sheet stays for the sibling Compliance
 * components — and for `SuperAdmin/ZoomHosts/CapacityPlannerTab`, which renders
 * its classes while importing no stylesheet at all.
 */

const STATUS_VARIANT = {
  pending: 'pending',
  in_progress: 'ongoing',
  overdue: 'destructive',
};

const DASH = <span className="tw-text-content-subtle">—</span>;

function daysUntil(date) {
  if (!date) return null;
  const ms = new Date(date).getTime() - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

/** "3d overdue" / "Due today" / "5d remaining" — the phrasing the old cell used. */
function dueLabel(dueDate) {
  if (!dueDate) return null;
  const days = daysUntil(dueDate);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return 'Due today';
  return `${days}d remaining`;
}

export default function StudentCompliancePage() {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getMyMandatoryAssignments();
        setAssignments(data?.assignments || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const blocking = useMemo(
    () => assignments.filter((a) => a.status === 'overdue' && a.mandatoryId?.blocking),
    [assignments]
  );

  const goToCourse = (a) => {
    const courseId = a.course?._id || a.course;
    if (!courseId) return;
    navigate(`/s/self-paced/${courseId}/learn`);
  };

  const columns = useMemo(() => [
    {
      key: 'training',
      label: 'Training',
      sortable: true,
      sortValue: (a) => a.mandatoryId?.name || '',
      render: (a) => (
        <span className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-font-medium tw-text-content">{a.mandatoryId?.name || '—'}</span>
          {a.mandatoryId?.blocking && (
            <StatusBadge status="destructive" className="tw-shrink-0">Blocking</StatusBadge>
          )}
        </span>
      ),
    },
    {
      key: 'course',
      label: 'Course',
      sortable: true,
      sortValue: (a) => a.course?.name || '',
      render: (a) => a.course?.name || DASH,
    },
    {
      key: 'dueDate',
      label: 'Due',
      sortable: true,
      // The DATE, not the label — "3d overdue" sorts alphabetically and files
      // 3 days after 30. This is the column a student actually orders by.
      sortValue: (a) => a.dueDate || null,
      render: (a) => {
        const label = dueLabel(a.dueDate);
        if (!label) return DASH;
        return (
          <span
            className="tw-inline-flex tw-items-center tw-gap-1"
            // ⚠️ IST explicitly — the box is UTC and a bare toLocaleString
            // names the previous day for anything after 05:30.
            title={new Date(a.dueDate).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
          >
            <Clock size={12} /> {label}
          </span>
        );
      },
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      sortValue: (a) => a.status || '',
      render: (a) => (
        <StatusBadge status={STATUS_VARIANT[a.status] || 'pending'}>{a.status}</StatusBadge>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: 120,
      pinned: 'right',
      resizable: false,
      // ⚠️ The row is clickable, so the CTA must stop the click reaching it —
      // otherwise the row handler fires too and the navigation runs twice.
      render: (a) => (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
          <AppButton variant="default" size="sm" onClick={() => goToCourse(a)}>
            <PlayCircle size={14} /> {a.status === 'in_progress' ? 'Continue' : 'Start'}
          </AppButton>
        </div>
      ),
    },
    // `goToCourse` closes over `navigate` only, which react-router keeps stable.
  ], []); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(
    () => sortRows(assignments, columns, sort),
    [assignments, columns, sort],
  );

  return (
    <div className="tw-flex tw-flex-col tw-gap-6">
      <PageHeader title="My Mandatory Training" icon={ShieldCheck} />

      {!!blocking.length && (
        <Callout tone="danger" role="alert" icon={<ShieldAlert size={14} />}>
          You have {blocking.length} overdue blocking training
          {blocking.length === 1 ? '' : 's'}. Other learning content is restricted
          until completion.
        </Callout>
      )}

      <AppDataTable
        fill={false}
        columns={columns}
        rows={rows}
        rowKey={(a) => a._id}
        loading={loading}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        onRowClick={goToCourse}
        emptyMessage="No mandatory training assigned to you."
      />
    </div>
  );
}
